/**
 * Serverfunktionen der versteckten Events-Seite.
 * Öffentlich: Seite lesen, anmelden, absagen. Intern: Pflege (Administration und Redaktion).
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { cancelTokenSchema, eventMessageSchema, eventRegistrationSchema, eventSaveSchema } from "@/lib/events";

export const sendAdminEventMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => eventMessageSchema.parse(input))
  .handler(async ({ context, data }) => {
    const { assertStaff } = await import("@/lib/landing-hundert-tage.access.server");
    await assertStaff(context.supabase, context.userId);
    const { sendEventMessage } = await import("@/lib/events.server");
    return sendEventMessage(context.supabase, context.userId, data);
  });

export const getPublicEvent = createServerFn({ method: "GET" }).handler(async () => {
  const { loadPublicEvent } = await import("@/lib/events.server");
  return loadPublicEvent();
});

export const registerEventParticipant = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => eventRegistrationSchema.parse(input))
  .handler(async ({ data }) => {
    const { registerForEvent } = await import("@/lib/events.server");
    return registerForEvent(data);
  });

export const lookupEventCancellation = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => cancelTokenSchema.parse(input))
  .handler(async ({ data }) => {
    const { lookupCancellation } = await import("@/lib/events.server");
    return lookupCancellation(data.token);
  });

export const cancelEventParticipation = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => cancelTokenSchema.parse(input))
  .handler(async ({ data }) => {
    const { cancelByToken } = await import("@/lib/events.server");
    return cancelByToken(data.token);
  });

const idSchema = z.object({ id: z.string().uuid() });

export const getAdminEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertStaff } = await import("@/lib/landing-hundert-tage.access.server");
    await assertStaff(context.supabase, context.userId);
    const { listAdminEvents } = await import("@/lib/events.server");
    return listAdminEvents(context.supabase);
  });

export const saveAdminEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => eventSaveSchema.parse(input))
  .handler(async ({ context, data }) => {
    const { assertStaff } = await import("@/lib/landing-hundert-tage.access.server");
    await assertStaff(context.supabase, context.userId);
    const { saveEvent } = await import("@/lib/events.server");
    return { id: await saveEvent(context.supabase, data) };
  });

export const activateAdminEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => idSchema.parse(input))
  .handler(async ({ context, data }) => {
    const { assertStaff } = await import("@/lib/landing-hundert-tage.access.server");
    await assertStaff(context.supabase, context.userId);
    const { activateEvent } = await import("@/lib/events.server");
    await activateEvent(context.supabase, data.id);
    return { ok: true };
  });

export const archiveAdminEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => idSchema.parse(input))
  .handler(async ({ context, data }) => {
    const { assertStaff } = await import("@/lib/landing-hundert-tage.access.server");
    await assertStaff(context.supabase, context.userId);
    const { archiveEvent } = await import("@/lib/events.server");
    await archiveEvent(context.supabase, data.id);
    return { ok: true };
  });

export const getAdminEventRegistrations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => idSchema.parse(input))
  .handler(async ({ context, data }) => {
    const { assertStaff } = await import("@/lib/landing-hundert-tage.access.server");
    await assertStaff(context.supabase, context.userId);
    const { listRegistrations } = await import("@/lib/events.server");
    return listRegistrations(context.supabase, data.id);
  });

export const cancelAdminEventRegistration = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => idSchema.parse(input))
  .handler(async ({ context, data }) => {
    const { assertStaff } = await import("@/lib/landing-hundert-tage.access.server");
    await assertStaff(context.supabase, context.userId);
    const { cancelRegistrationByStaff } = await import("@/lib/events.server");
    await cancelRegistrationByStaff(context.supabase, data.id);
    return { ok: true };
  });
